import { useState } from 'react';
import { DatePicker, DatePickerProps } from '@mui/x-date-pickers';

import { Clear } from '@mui/icons-material';
import { FormControlOwnProps, IconButtonProps, Typography } from '@mui/material';

import { useFormContext } from './Form.Context';

import { camelCaseToUserText, getColorCss } from '@dw/utils';

import { HStack } from '../HStack';
import { IconButton } from '../IconButton';

import { Dayjs } from '@dw/dayjs';

type TDatePickerValue = Dayjs | null | undefined;

interface IFormDateInputProps<T> extends Omit<DatePickerProps<Dayjs>, 'name' | 'label' | 'disabled'> {
    name: keyof T;
    label?: string;
    disabled?: boolean;
    required?: boolean;
    modifier?: 'startOfDay' | 'endOfDay';
    helperText?: string;
    margin?: FormControlOwnProps['margin'];
}
export function FormDateInput<T>(props: IFormDateInputProps<T>) {

    const {
        name,
        label,
        disabled,
        required,
        modifier,
        helperText,
        margin,
        ...datePickerProps
    } = props;

    const {
        formData,
        handleFormChange,
        disabled: formDisabled,
        formErrors,
        customFormErrors,
        submitAttempted
    } = useFormContext<T>();

    const [pickerOpen, setPickerOpen] = useState(false);

    const handleInputChange = (value: TDatePickerValue) => {
        let newValue = value;
        if (newValue && modifier === 'startOfDay') {
            newValue = newValue.startOf('day');
        } else if (newValue && modifier === 'endOfDay') {
            newValue = newValue.endOf('day');
        }

        handleFormChange(name, newValue);
    };

    const handleOpenPicker = () => {
        if (disabled || formDisabled) {
            return;
        }
        setPickerOpen(true);
    }

    const CustomButton = (props: IconButtonProps) => disabled || formDisabled ? null : (
        <HStack breakpoint={false} columnGap={0}>
            <IconButton icon={props.children} size={props.size} color={props.color} onClick={handleOpenPicker} />

            {formData[name] && (
                <IconButton icon={<Clear />} size={props.size} color={props.color} onClick={(e) => {

                    e.stopPropagation();
                    handleInputChange(null);
                }} />
            )}
        </HStack>
    );

    return (
        <DatePicker
            {...datePickerProps}
            label={label ?? camelCaseToUserText(String(name))}
            format={props.format}
            views={['year', 'month', 'day']}
            value={formData[name] as TDatePickerValue}
            onChange={handleInputChange}
            showDaysOutsideCurrentMonth
            open={pickerOpen}
            onClose={() => setPickerOpen(false)}
            slots={{
                openPickerButton: CustomButton
            }}
            slotProps={{
                textField: {
                    margin: margin,
                    error: submitAttempted && (formErrors[name] || !!customFormErrors[name]),
                    required: required,
                    helperText: submitAttempted && !!customFormErrors[name] ? (
                        <Typography variant='caption' color='error'>{customFormErrors[name]}</Typography>
                    ) : helperText ? (
                        <Typography variant='caption'>{helperText}</Typography>
                    ) : null
                },
                day: {
                    sx: {
                        "&.Mui-disabled:not(.Mui-selected)": {
                            color: getColorCss('dark', 0.2),
                            opacity: 1, 
                        },
                        "&.MuiPickersDay-today": {
                            border: `1px solid ${getColorCss('primary')}`,
                            color: getColorCss('primary'),
                            fontWeight: 500
                        },
                        "&.Mui-selected": {
                            fontWeight: 600,
                            fontSize: '0.9rem',
                            backgroundColor: getColorCss('primary'),
                            color: getColorCss('light')
                        }
                    }
                },
                monthButton: {
                    sx: {
                        "&.Mui-disabled": {
                            color: getColorCss('dark', 0.2),
                            opacity: 1,
                        },
                        "&.Mui-selected": {
                            fontWeight: 500,
                            fontSize: '1.1rem',
                            backgroundColor: getColorCss('primary'),
                            color: getColorCss('light')
                        }
                    }
                },
                popper: {
                    popperOptions: {
                        modifiers: [
                            {
                                name: 'flip',
                                options: {
                                    altBoundary: false,
                                    fallbackPlacements: ['right', 'left'], // This is to prevent popper from being cut off at bottom or top, and just giving it the option to try to the left and right of the input as well
                                }
                            },
                            {
                              name: 'preventOverflow',
                              enabled: true,
                              options: {
                                altAxis: true,
                                altBoundary: true,
                                tether: true,
                                rootBoundary: 'document',
                                padding: 8,
                              },
                            }
                        ]
                    }
                }
            }}
            disabled={disabled || formDisabled}
        />
    );
};